import { ScopeContext, useManager, useScope } from 'dreamstate';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { rem, Modal, ModalContent, Paragraph } from '@vimeo/bokeh';
import { createInteractionDomName } from '@vimeo/cloud-live/libs/shared/utils/dom/identifying';
import { translations } from '@vimeo/cloud-live/translations';
import { IBaseDomElementProps } from '@vimeo/core/types/dom';
import { Optional } from '@vimeo/core/types/generic';
import { queryScopeGctlConfig } from '@vimeo/live-core/api/base';
import { getSessionIngestStatus } from '@vimeo/live-core/api/live_session';
import { environmentConfig } from '@vimeo/live-core/configs/EnvironmentConfig';
import { useInterval } from '@vimeo/live-core/hooks/general/useInterval';
import {
  IInteractionSessionContext,
  InteractionSessionManager,
} from '@vimeo/live-core/store/interaction/session';
import { EApiErrorCode } from '@vimeo/live-core/types/error';
import { EIngestStatus, ISessionInfo } from '@vimeo/live-core/types/event';
import { CallablePlaceholder } from '@vimeo/live-core/types/general';
import {
  EInteractionAuthOption,
  EInteractionRoomPrivacy,
} from '@vimeo/live-core/types/interaction_room';
import { getIframeReferrer } from '@vimeo/live-core/utils/embed/embed_referrer';
import { isApiError } from '@vimeo/live-core/utils/error';
import { default as useOnClickOutside } from '@vimeo/ui/hooks/use-outside-click/useOnClickOutside';

import { useRegistrantLoginCookie } from '../../../../hooks/login/useRegistrantLoginCookie';
import { useInteractionLoginContext } from '../shared/useInteractionLoginContext';
import { useInteractionLoginOption } from '../shared/useInteractionLoginOption';

type TRegistrantActionType = 'registration' | 'login';

interface IRegistrantLoginProps extends IBaseDomElementProps {
  interactionSessionContext?: IInteractionSessionContext;
}

/**
 * Login as registrant with email.
 */
export function RegistrantLogin({
  id = createInteractionDomName('registrant-login'),
  interactionSessionContext: {
    roomPassword,
    roomHashedPassword,
    roomUnlistedHash,
    // eslint-disable-next-line react-hooks/rules-of-hooks
  } = useManager(InteractionSessionManager),
}: IRegistrantLoginProps) {
  const scope: ScopeContext = useScope();
  const modalRef = useRef(null);
  const [ingestStatus, setIngestStatus] = useState(EIngestStatus.UNKNOWN);
  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  const [registrantActionType, setRegistrantActionType] =
    useState<TRegistrantActionType>('registration');

  const {
    sessionId,
    setAccessBlocked,
    setJoinLabels,
    setIsTermsLabelEnabled,
    setJoinLabelIconEnabled,
  } = useInteractionLoginContext();
  const {
    isLoading,
    isLoginFailed,
    isRoomUserAuthAttempted,
    error,
    errorMessage,
    roomInfo,
    log,
    onLogin,
    onResetError,
  } = useInteractionLoginOption(EInteractionAuthOption.REGISTRANT);

  const isLive: boolean = ingestStatus === EIngestStatus.STREAMING;
  const isEnded: boolean = ingestStatus === EIngestStatus.ENDED;
  const isUnlistedEvent: boolean = Boolean(roomUnlistedHash);
  const isEmbedOnly: boolean = Boolean(
    roomInfo?.settings.privacy === EInteractionRoomPrivacy.EMBED_ONLY,
  );
  const apiEventId: string = isUnlistedEvent
    ? `${sessionId}:${roomUnlistedHash}`
    : (sessionId as string);

  const roomRegistrantUuid: Optional<string> = useRegistrantLoginCookie(true, apiEventId);

  const registerPageUrl: string = useMemo(() => {
    const url: string = `${environmentConfig.PROTOCOL}//${environmentConfig.HOST}/live/interaction_tools/events/${sessionId}/register`;
    const searchParams: URLSearchParams = new URLSearchParams();

    if (registrantActionType === 'login') {
      searchParams.set('previewType', 'login_screen');
    }

    if (roomUnlistedHash) {
      searchParams.set('authRoomUnlistedHash', roomUnlistedHash);
    }

    if (roomPassword || roomHashedPassword) {
      searchParams.set('authRoomPassword', (roomPassword || roomHashedPassword) as string);
    }

    if (environmentConfig.IS_IFRAME && isEmbedOnly) {
      searchParams.set('pageReferrer', getIframeReferrer() as string);
    }

    const queryString: string = searchParams.toString();

    return queryString.length ? `${url}?${queryString}` : url;
  }, [
    sessionId,
    registrantActionType,
    roomUnlistedHash,
    roomPassword,
    roomHashedPassword,
    isEmbedOnly,
  ]);

  const onModalClose = useCallback(() => setModalOpen(false), []);
  const onModalOpen = useCallback(() => setModalOpen(true), []);

  const onRegisterLinkClick = useCallback(() => {
    setRegistrantActionType('registration');
    onModalOpen();
  }, [onModalOpen]);

  const onSignInLinkClick = useCallback(() => {
    setRegistrantActionType('login');
    onModalOpen();
  }, [onModalOpen]);

  const onLoadIngestStatus = useCallback(async () => {
    if (!roomInfo?.metadata.connections.ingestStatus) {
      return;
    }

    const response: ISessionInfo = await getSessionIngestStatus({
      connection: roomInfo?.metadata.connections.ingestStatus,
      gctlConfig: queryScopeGctlConfig(scope),
      fields: ['id', 'ingest.status'],
    });

    setIngestStatus(response.ingest.status);
  }, [roomInfo?.metadata.connections.ingestStatus, scope]);

  useOnClickOutside(modalRef, onModalClose, null, [onModalClose]);

  // Block access on some critical errors like event end or rate limit, if event is ended block login.
  useEffect(() => {
    const isEventEndedError: boolean =
      isApiError(error) &&
      error.data.body.errorCode === EApiErrorCode.ROOM_AUTHORIZATION_FAILED_SESSION_ENDED;

    if (isEventEndedError || isEnded) {
      setAccessBlocked(translations.eventHasEnded);
    }
  }, [error, isEnded, setAccessBlocked]);

  useEffect(() => {
    if (!isRoomUserAuthAttempted && !isLoading && isLive && roomRegistrantUuid) {
      onModalClose();
      log.info('Attempt of registrant auto-login:', roomRegistrantUuid);
      onLogin({ roomRegistrantUuid }).catch((error) => log.error('Attempt failed:', error));
    }
  }, [isLive, isLoading, isRoomUserAuthAttempted, log, onLogin, onModalClose, roomRegistrantUuid]);

  useEffect(() => {
    if (isLive) {
      setJoinLabels(translations.registrationRequired);
      setJoinLabelIconEnabled(false);
      setIsTermsLabelEnabled(false);
    } else {
      setJoinLabels(translations.joinTheAudienceWhenTheEventIsLive);
      setIsTermsLabelEnabled(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLive]);

  useInterval(Boolean(!isLive && roomInfo?.metadata.connections.ingestStatus), onLoadIngestStatus, {
    checkCallable: true,
    initialize: true,
    period: 4000,
  });

  useEffect(() => {
    onResetError();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Render notifications in layer above.
  if (!isLive) {
    return null;
  }

  return (
    <>
      <Modal isOpen={isModalOpen} onClose={CallablePlaceholder}>
        <ModalContent
          id={createInteractionDomName(id, 'modal-content')}
          ref={modalRef}
          width={'100vw'}
          maxWidth={rem(670)}
          height={'100vh'}
          maxHeight={rem(600)}
          backgroundColor={'grayscale.800'}
        >
          <iframe
            loading={'lazy'}
            src={registerPageUrl}
            width={'100%'}
            height={'100%'}
            frameBorder={0}
          />
        </ModalContent>
      </Modal>

      <Paragraph id={id} size={'lg'} textAlign={'center'} color={'text-primary'}>
        {translations.registerOrSignIntoEventToJoinAudience(onRegisterLinkClick, onSignInLinkClick)}
      </Paragraph>

      {isLoginFailed ? (
        <Paragraph id={createInteractionDomName(id, 'error')} size={'xs'} color={'red.500'}>
          {errorMessage}
        </Paragraph>
      ) : null}
    </>
  );
}
